/**
 * ConversationListenScreen.js
 * ============================
 * Caregiver-operated listening screen for Conversation Mode.
 *
 * Flow:
 *   1. Caregiver taps "Start Conversation" → Web Speech API begins capturing
 *   2. Live transcript appears and can be edited in the textarea
 *   3. Caregiver taps ✓ → LLM generates 3 responses → navigate to /conversation/respond
 *   4. Caregiver taps ⊖ → clear transcript, allow re-recording
 *
 * This screen is entirely touch-operated (no gaze). It renders on cream #f8f7f4
 * with Space Mono font, matching the Figma "ai mode / Listening Mode Screen 1" design.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateAiOptions } from './aiApi';
import { useGazeConfig, COLOR_MAP } from './GazeConfigContext';
import './ConversationListenScreen.css';

// ── Sound bar heights (from Figma design, in px) ──────────────────────────
const BAR_HEIGHTS = [17, 24, 13, 35, 42, 26, 96, 66, 32, 31, 67, 59, 21, 13, 22, 31, 17, 10];

export default function ConversationListenScreen() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const autoStart = location.state?.autoStart ?? false;

  const { config } = useGazeConfig();
  const colorVars = {
    '--cls-bg':   COLOR_MAP[config.backgroundColor] ?? '#f8f7f4',
    '--cls-text': COLOR_MAP[config.textColor]        ?? '#111111',
  };

  // ── Speech recognition state ─────────────────────────────────────────────
  const [isListening,   setIsListening]   = useState(false);
  const [transcript,    setTranscript]    = useState('');
  const [isGenerating,  setIsGenerating]  = useState(false);
  const [error,         setError]         = useState('');

  const recognitionRef     = useRef(null);
  const isListeningRef     = useRef(false); // mirror for use inside event callbacks
  const finalTranscriptRef = useRef('');    // accumulates only final (confirmed) chunks

  // ── Swipe-up + back-button → /landing ────────────────────────────────────
  useEffect(() => {
    // Push a history entry so the back button fires popstate instead of leaving the app
    window.history.pushState(null, '', window.location.href);
    const onPopState = () => navigate('/landing', { replace: true });
    window.addEventListener('popstate', onPopState);

    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchEnd   = (e) => {
      const deltaY = touchStartY - e.changedTouches[0].clientY;
      if (deltaY > 60) navigate('/landing'); // swipe up ≥ 60px
    };
    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchend',   onTouchEnd);

    return () => {
      window.removeEventListener('popstate',    onPopState);
      window.removeEventListener('touchstart',  onTouchStart);
      window.removeEventListener('touchend',    onTouchEnd);
    };
  }, [navigate]);

  // ── Check browser support ─────────────────────────────────────────────────
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const speechSupported = Boolean(SpeechRecognition);

  // ── Initialise speech recognition ────────────────────────────────────────
  useEffect(() => {
    if (!speechSupported) return;

    const recognition = new SpeechRecognition();
    recognition.continuous      = true;
    recognition.interimResults  = true;
    recognition.lang            = 'en-US';

    recognition.onresult = (event) => {
      // Separate new final chunks from the current interim chunk.
      // finalTranscriptRef holds all previously confirmed text — never reset it
      // mid-session. interim is only the in-progress word(s) for this callback;
      // it gets replaced (not appended) every time onresult fires.
      let newFinal = '';
      let interim  = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinal += chunk + ' ';
        } else {
          interim = chunk; // only the latest interim slice
        }
      }
      if (newFinal) {
        finalTranscriptRef.current = (finalTranscriptRef.current + newFinal).trimStart();
      }
      // Display = confirmed final text + current interim preview
      setTranscript((finalTranscriptRef.current + interim).trimStart());
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') return; // benign timeout
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      isListeningRef.current = false;
    };

    recognition.onend = () => {
      // Auto-restart if still in listening mode (browser stops after ~60 s of silence)
      if (isListeningRef.current) {
        try { recognition.start(); } catch (_) {}
      }
    };

    recognitionRef.current = recognition;
    return () => {
      recognition.onend = null; // prevent restart on unmount
      try { recognition.stop(); } catch (_) {}
    };
  }, [speechSupported]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-start when arriving from the mic button on the response screen ───
  useEffect(() => {
    if (!autoStart || !speechSupported) return;
    // recognitionRef is populated by the setup effect above.
    // A short delay ensures the recognition instance is fully ready before start().
    const t = setTimeout(() => {
      if (!isListeningRef.current && recognitionRef.current) {
        finalTranscriptRef.current = '';
        setTranscript('');
        setIsListening(true);
        isListeningRef.current = true;
        try { recognitionRef.current.start(); } catch (_) {}
      }
    }, 150);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start listening ───────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    if (!speechSupported || isListening) return;
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
    setIsListening(true);
    isListeningRef.current = true;
    try {
      recognitionRef.current?.start();
    } catch (_) {}
  }, [speechSupported, isListening]);

  // ── Stop listening ────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    setIsListening(false);
    isListeningRef.current = false;
    recognitionRef.current?.stop();
  }, []);

  // ── Cancel: stop and clear ────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    stopListening();
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
  }, [stopListening]);

  // ── Confirm: call LLM and navigate ───────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    const text = transcript.trim();
    if (!text) {
      setError('Please record or type a message first.');
      return;
    }
    stopListening();
    setIsGenerating(true);
    setError('');
    try {
      const responses = await generateAiOptions({ caregiverText: text });
      navigate('/conversation/respond', {
        state: { responses, caregiverText: text },
      });
    } catch (err) {
      setError(err.message || 'Failed to generate responses. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [transcript, stopListening, navigate]);

  // ── Render ────────────────────────────────────────────────────────────────
  const hasTranscript = transcript.trim().length > 0;

  return (
    <div className="cls-root" style={colorVars}>

      {/* ── Start / status pill ─────────────────────────────────────────── */}
      <div className="cls-top">
        <button
          className={`cls-start-btn${isListening ? ' cls-start-btn--active' : ''}`}
          onClick={isListening ? stopListening : startListening}
          disabled={isGenerating}
          aria-label={isListening ? 'Stop recording' : 'Start conversation recording'}
        >
          {isGenerating
            ? 'Generating…'
            : isListening
              ? 'Listening — tap to stop'
              : 'Start Conversation'}
        </button>
      </div>

      {/* ── Sound bar visualiser ────────────────────────────────────────── */}
      <div className="cls-soundbar" aria-hidden="true">
        {BAR_HEIGHTS.map((h, i) => (
          <div
            key={i}
            className={`cls-bar${isListening ? ' cls-bar--active' : ''}`}
            style={{
              '--bar-height': `${h}px`,
              '--bar-delay': `${(i * 40) % 600}ms`,
            }}
          />
        ))}
      </div>

      {/* ── Status label ────────────────────────────────────────────────── */}
      <p className="cls-status">
        {isGenerating
          ? 'Generating responses…'
          : isListening
            ? 'Listening…'
            : hasTranscript
              ? 'Review your message'
              : speechSupported
                ? 'Tap to start recording'
                : 'Microphone not supported — type your message below'}
      </p>

      {/* ── Stop button (shown while listening) ─────────────────────────── */}
      {isListening && (
        <button
          className="cls-stop-btn"
          onClick={stopListening}
          aria-label="Stop recording"
        >
          <span className="cls-stop-square" aria-hidden="true" />
        </button>
      )}

      {/* ── Transcript area ──────────────────────────────────────────────── */}
      <div className="cls-transcript-wrap">
        <textarea
          className="cls-transcript"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder={
            speechSupported
              ? 'Your words will appear here — you can edit before sending.'
              : 'Type the caregiver\'s message here…'
          }
          disabled={isGenerating}
          rows={5}
          aria-label="Caregiver message transcript — editable"
        />
        {/* Gradient fade over the bottom of long text */}
        <div className="cls-transcript-fade" aria-hidden="true" />
      </div>

      {/* ── Error message ────────────────────────────────────────────────── */}
      {error && <p className="cls-error" role="alert">{error}</p>}

      {/* ── Action buttons: confirm ✓ and cancel ⊖ ───────────────────────── */}
      <div className="cls-actions">
        <button
          className="cls-action-btn cls-action-btn--cancel"
          onClick={handleCancel}
          disabled={isGenerating}
          aria-label="Cancel — discard and re-record"
        >
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>

        <button
          className="cls-action-btn cls-action-btn--confirm"
          onClick={handleConfirm}
          disabled={isGenerating || !hasTranscript}
          aria-label="Confirm — send to AI and generate responses"
        >
          {isGenerating ? (
            <span className="cls-spinner" aria-hidden="true" />
          ) : (
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              strokeLinejoin="round">
              <polyline points="4,12 10,18 20,6" />
            </svg>
          )}
        </button>
      </div>

    </div>
  );
}
